/**
 * Core Web Vitals RUM (Real User Monitoring) — §P1-D of the 2026 UI/UX
 * standards roadmap. CrUX is Chrome-only, so our Capacitor WebView (iOS
 * Safari / Android) users are invisible to it; this ships lab-independent
 * field metrics (LCP, INP, CLS, FCP, TTFB) from every real session.
 *
 * Privacy (data-handling guardrail): the payload NEVER carries a raw URL. The
 * route is scrubbed to its template — every UUID / ULID / long numeric id is
 * replaced with a placeholder — so a metric can never leak a property/user id
 * or explode metric cardinality. No user identifier, query string, or hash is
 * sent. Transport is best-effort and silent: a RUM failure must never surface
 * to the app.
 *
 * Opt-in: no-op unless `VITE_RUM_ENDPOINT` is set (empty on every tier today),
 * so wiring the collector is inert until an ingest endpoint is provisioned.
 */

// Order matters: UUIDs contain hyphens (so the contiguous-alnum ULID pattern
// can't match them) — scrub hyphenated UUIDs first, then a hyphenless 32-hex id,
// then ULIDs, then bare long numbers.
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
// Hyphenless 32-hex id — defensive: current routes only ever carry hyphenated
// UUIDs, but a raw id leaking in (3rd-party redirect, future feature) must not
// reach the payload as PII.
const RAW_HEX_ID_RE = /\b[0-9a-f]{32}\b/gi;
// Crockford base32, 26 chars (excludes I, L, O, U). This app lowercases ULID
// keys, but match either case defensively.
const ULID_RE = /\b[0-9A-HJKMNP-TV-Za-hjkmnp-tv-z]{26}\b/g;
// Bare numeric segments of 4+ digits (ids, timestamps) — leaves small ordinals
// like a page number alone.
const LONG_NUM_RE = /\b\d{4,}\b/g;

/**
 * Reduce a raw pathname to a low-cardinality route template safe to report.
 * Pure and exported for testing — this is the privacy-critical seam.
 *
 * Known limitation (untriggered by current routes): the ULID pass can also
 * match a 26-char static segment built only from Crockford chars. Every live
 * route uses hyphenated `:uuid` params or kebab slugs (whose hyphens break the
 * run), so this doesn't fire today; revisit if a non-kebab 26-char static
 * segment is ever added.
 */
export function scrubRouteTemplate(pathname: string): string {
  const scrubbed = pathname
    .replace(UUID_RE, ':uuid')
    .replace(RAW_HEX_ID_RE, ':uuid')
    .replace(ULID_RE, ':id')
    .replace(LONG_NUM_RE, ':n');
  return scrubbed || '/';
}

interface WebVitalMetric {
  name: string;
  value: number;
  rating: string;
  id: string;
  navigationType: string;
}

export interface ReportWebVitalsOptions {
  /** Ingest endpoint. Defaults to `VITE_RUM_ENDPOINT`; empty disables RUM. */
  endpoint?: string;
  /** Route-template resolver. Defaults to scrubbing `window.location.pathname`. */
  route?: () => string;
}

function send(endpoint: string, route: string, metric: WebVitalMetric): void {
  const body = JSON.stringify({
    metric: metric.name,
    value: Math.round(metric.value * 1000) / 1000,
    rating: metric.rating,
    route,
    id: metric.id,
    navigationType: metric.navigationType,
    ts: Date.now(),
  });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // sendBeacon survives the unload that fires the final CLS/LCP report.
      navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
    } else {
      void fetch(endpoint, {
        method: 'POST',
        body,
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
      }).catch(() => {
        /* best-effort */
      });
    }
  } catch {
    // RUM is fire-and-forget; never let a transport error reach the app.
  }
}

/**
 * Register the Core Web Vitals listeners. No-op on the server, when no endpoint
 * is configured, or if the `web-vitals` module fails to load.
 */
export function reportWebVitals(options: ReportWebVitalsOptions = {}): void {
  const endpoint =
    options.endpoint ?? ((import.meta.env.VITE_RUM_ENDPOINT as string | undefined) || '');
  if (!endpoint || typeof window === 'undefined') {
    return;
  }

  // Capture the load route NOW, not at report time. web-vitals (v4, no
  // soft-navigation API) measures the INITIAL page load, but finalizes
  // LCP/CLS/INP late (on visibilitychange/pagehide) — by then an SPA route
  // change may have moved location.pathname, so reading it at report time would
  // misattribute the metric to whatever route the user is on when they leave.
  // Per-soft-navigation attribution would need the experimental soft-navs API
  // plus per-route metric resets — out of scope until a real ingest endpoint is
  // provisioned (VITE_RUM_ENDPOINT is empty everywhere today).
  const loadRoute = options.route
    ? options.route()
    : scrubRouteTemplate(window.location.pathname);

  void import('web-vitals')
    .then(({ onLCP, onINP, onCLS, onFCP, onTTFB }) => {
      const report = (metric: WebVitalMetric): void => send(endpoint, loadRoute, metric);
      onLCP(report);
      onINP(report);
      onCLS(report);
      onFCP(report);
      onTTFB(report);
    })
    .catch(() => {
      // web-vitals is an optional dependency of the RUM path; ignore load failure.
    });
}
